import { FormControlLabel, Switch, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Button } from '@/components/Button';
import { useAlertsStore, useUserStore } from '@/configs/store';
import { useLocalStore } from '@/hooks/useLocalStore';
import { IssueList, IssueListControls, useIssueListSettings } from '@/tracker/features/issueList';
import { useTrackerStore } from '@/tracker/model';
import { Loader } from '@/tracker/ui/Loader';

import { MyIssuesStore } from './model/MyIssuesStore';

import s from './MyIssuesPage.module.scss';

const MyIssuesPage = observer(() => {
  const alertsStore = useAlertsStore();
  const trackerStore = useTrackerStore();
  const { user } = useUserStore();
  const { store } = useLocalStore(
    () => new MyIssuesStore({ alertsStore, trackerStore, userId: user?.id ?? null }),
  );
  const { settings, toggleColumn, setGroupBy } = useIssueListSettings({
    key: 'myIssues',
    defaults: {
      columns: ['priority', 'type', 'status', 'queue', 'updateTime'],
      groupBy: 'none',
    },
  });

  useEffect(() => {
    store.load();
  }, [store]);

  const isEmpty = store.issues.length === 0;

  const renderContent = () => {
    if (store.isLoading && store.totalCount === 0) return <Loader />;

    if (store.isError && isEmpty) {
      return (
        <div className={s.page__empty}>
          <Typography color="text.secondary">Не удалось загрузить задачи</Typography>
          <Button onClick={store.load}>Повторить</Button>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className={s.page__empty}>
          <Typography color="text.secondary">
            {store.query ? 'Ничего не нашлось' : 'На вас нет открытых задач'}
          </Typography>
        </div>
      );
    }

    return (
      <IssueList
        issues={store.issues}
        settings={settings}
        sort={store.sort}
        onSortChange={store.setSort}
        isLoading={store.isLoading}
      />
    );
  };

  return (
    <div className={s.page}>
      <Typography variant="h5" fontWeight="bold">
        Мои задачи
      </Typography>

      <IssueListControls
        query={store.query}
        onQueryChange={store.setQuery}
        counter={`${store.issues.length} / ${store.totalCount}`}
        settings={settings}
        onGroupByChange={setGroupBy}
        onToggleColumn={toggleColumn}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={store.showClosed}
              onChange={(event) => store.setShowClosed(event.target.checked)}
            />
          }
          label={`Показывать закрытые (${store.closedCount})`}
        />
      </IssueListControls>

      {renderContent()}
    </div>
  );
});

export { MyIssuesPage };
